import { z } from "zod";
import { ClippingSchema, NodeChatExcerptSchema, NodeDecisionSchema, NodeFactSchema, NodeSupersededSchema, SOURCE_REF_KINDS, } from "./ideation-maps.js";
import { SubjectAssociationSchema, } from "./subject-associations.js";
import { ConnectedKnowledgeProjectionSchema, emptyConnectedKnowledge, } from "./subject-connected-knowledge.js";
import { deriveSubjectIntelligence, SubjectIntelligenceSchema, } from "./subject-intelligence.js";
/**
 * Phase 4A — Subject Context Bundle.
 *
 * Deterministic assembler output. Canonical graph fields stay distinct from
 * retrieved object prose. Evidence (`sourceRefs`) and operational associations
 * stay in separate arrays even when they point at the same object.
 */
export const SUBJECT_CONTEXT_BUNDLE_VERSION = 1;
export const SUBJECT_CONTEXT_RETRIEVAL_CHAR_BUDGET = 24_000;
export const SUBJECT_CONTEXT_MAX_EXCERPT_CHARS = 4_000;
export const SUBJECT_CONTEXT_NEIGHBOUR_EXCERPT_CHARS = 240;
export const SubjectCitationStatusSchema = z.enum(["retrieved", "identified", "unavailable"]);
export const SubjectBundleEvidenceKindSchema = z.enum(SOURCE_REF_KINDS);
export function subjectIdentityCitationId(subjectId) {
    return `subject:${subjectId}`;
}
export function subjectSummaryCitationId(subjectId) {
    return `subject:${subjectId}:summary`;
}
export function subjectBodyCitationId(subjectId) {
    return `subject:${subjectId}:body`;
}
function fnv1aHex(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
/**
 * Collision-resistant fact citation. Keys are unique on the write path, but
 * competing recorded values can share key+asOf; identity includes the full
 * canonical fact tuple (no new table).
 */
export function factCitationId(fact) {
    const identity = [
        fact.key,
        fact.asOf,
        fact.value,
        fact.unit ?? "",
        fact.source,
        fact.recordedAt ?? "",
        fact.recordedBy ?? "",
    ].join("\u001f");
    return `fact:${fact.key}:${fact.asOf}:${fnv1aHex(identity)}`;
}
export function decisionCitationId(index, at) {
    return `decision:${index}:${at}`;
}
export function questionCitationId(nodeId) {
    return `question:${nodeId}`;
}
export function neighbourCitationId(nodeId, relation) {
    return `neighbour:${nodeId}:${relation}`;
}
export function evidenceCitationId(kind, ref) {
    return `evidence:${kind}:${ref}`;
}
export function associationCitationId(objectType, objectId) {
    return `assoc:${objectType}:${objectId}`;
}
export function clippingCitationId(clippingId) {
    return `clipping:${clippingId}`;
}
export function chatExcerptCitationId(sessionId, index) {
    return `chat:${sessionId}:${index}`;
}
export function mapCitationId(mapId) {
    return `map:${mapId}`;
}
export const SubjectCitedFactSchema = NodeFactSchema.extend({
    citationId: z.string().min(1),
});
export const SubjectCitedDecisionSchema = NodeDecisionSchema.extend({
    citationId: z.string().min(1),
});
export const SubjectCitedClippingSchema = ClippingSchema.extend({
    citationId: z.string().min(1),
});
export const SubjectCitedChatExcerptSchema = NodeChatExcerptSchema.extend({
    citationId: z.string().min(1),
});
export const SubjectRelatedRecordSchema = z.object({
    citationId: z.string().min(1),
    nodeId: z.string().min(1),
    label: z.string(),
    kind: z.string(),
    relation: z.string(),
    excerpt: z.string(),
});
export const SubjectEvidenceRecordSchema = z.object({
    citationId: z.string().min(1),
    kind: SubjectBundleEvidenceKindSchema,
    ref: z.string().min(1),
    span: z.string().nullable(),
    title: z.string().nullable(),
    status: SubjectCitationStatusSchema,
    unavailableReason: z.string().nullable(),
});
export const SubjectCitedAssociationSchema = SubjectAssociationSchema.extend({
    citationId: z.string().min(1),
});
export const SubjectBundleMapSchema = z.object({
    citationId: z.string().min(1),
    mapId: z.string().min(1),
    title: z.string(),
});
export const SubjectContextCanonicalSchema = z.object({
    identity: z.object({
        citationId: z.string().min(1),
        id: z.string().min(1),
        label: z.string(),
        kind: z.string(),
        aliases: z.array(z.string()),
        status: z.string(),
    }),
    summary: z.object({
        citationId: z.string().min(1),
        text: z.string(),
    }),
    body: z.object({
        citationId: z.string().min(1),
        text: z.string(),
    }),
    superseded: NodeSupersededSchema.nullable(),
    facts: z.array(SubjectCitedFactSchema),
    decisions: z.array(SubjectCitedDecisionSchema),
    openQuestions: z.array(SubjectRelatedRecordSchema),
    related: z.array(SubjectRelatedRecordSchema),
    evidence: z.array(SubjectEvidenceRecordSchema),
    associations: z.array(SubjectCitedAssociationSchema),
    maps: z.array(SubjectBundleMapSchema),
    clippings: z.array(SubjectCitedClippingSchema),
    chatExcerpts: z.array(SubjectCitedChatExcerptSchema),
});
export const SubjectRetrievalLayerSchema = z.enum(["evidence", "association"]);
export const SubjectRetrievalExcerptSchema = z.object({
    citationId: z.string().min(1),
    layer: SubjectRetrievalLayerSchema,
    kind: z.string().min(1),
    id: z.string().min(1),
    title: z.string().nullable(),
    status: SubjectCitationStatusSchema,
    text: z.string().nullable(),
    unavailableReason: z.string().nullable(),
});
export const SubjectContextBundleSchema = z.object({
    bundleVersion: z.literal(SUBJECT_CONTEXT_BUNDLE_VERSION),
    subjectId: z.string().min(1),
    generatedAt: z.string().datetime(),
    truncated: z.boolean(),
    depth: z.number().int().min(0),
    retrievalCharBudget: z.number().int().min(0),
    canonical: SubjectContextCanonicalSchema,
    retrieval: z.object({
        excerpts: z.array(SubjectRetrievalExcerptSchema),
    }),
    intelligence: SubjectIntelligenceSchema,
    connected: ConnectedKnowledgeProjectionSchema,
});
function clipExcerpt(text, max) {
    if (!text)
        return "";
    if (text.length <= max)
        return text;
    return text.slice(0, max);
}
function applyRetrievalBudget(excerpts, budget) {
    let remaining = budget;
    let truncated = false;
    const out = [];
    for (const excerpt of excerpts) {
        if (excerpt.text == null || excerpt.text.length === 0) {
            out.push(excerpt);
            continue;
        }
        if (remaining <= 0) {
            truncated = true;
            out.push({
                ...excerpt,
                text: null,
                unavailableReason: excerpt.unavailableReason ?? "truncated",
            });
            continue;
        }
        if (excerpt.text.length <= remaining) {
            remaining -= excerpt.text.length;
            out.push(excerpt);
            continue;
        }
        truncated = true;
        out.push({ ...excerpt, text: excerpt.text.slice(0, remaining) });
        remaining = 0;
    }
    return { excerpts: out, truncated };
}
/** Pure assembler — no I/O. Tests inject a frozen `generatedAt`. */
export function composeSubjectContextBundle(gathered) {
    const budget = gathered.retrievalCharBudget ?? SUBJECT_CONTEXT_RETRIEVAL_CHAR_BUDGET;
    const subjectId = gathered.node.id;
    const related = gathered.related.map((row) => ({
        citationId: row.kind === "open-question" && row.relation === "questions"
            ? questionCitationId(row.nodeId)
            : neighbourCitationId(row.nodeId, row.relation),
        nodeId: row.nodeId,
        label: row.label,
        kind: row.kind,
        relation: row.relation,
        excerpt: clipExcerpt(row.excerpt, SUBJECT_CONTEXT_NEIGHBOUR_EXCERPT_CHARS),
    }));
    const evidence = gathered.evidence
        .filter((row) => row.unavailableReason !== "restricted")
        .map((row) => ({
        citationId: evidenceCitationId(row.kind, row.ref),
        kind: row.kind,
        ref: row.ref,
        span: row.span,
        title: row.title,
        status: row.status,
        unavailableReason: row.unavailableReason,
    }));
    const restrictedAssocKeys = new Set(gathered.associationRetrieval
        .filter((row) => row.unavailableReason === "restricted")
        .map((row) => `${row.objectType}:${row.objectId}`));
    const associations = gathered.associations
        .filter((row) => !restrictedAssocKeys.has(`${row.objectType}:${row.objectId}`))
        .map((row) => ({
        ...row,
        citationId: associationCitationId(row.objectType, row.objectId),
    }));
    const retrievalExcerpts = [
        ...gathered.evidence
            .filter((row) => row.unavailableReason !== "restricted")
            .map((row) => ({
            citationId: evidenceCitationId(row.kind, row.ref),
            layer: "evidence",
            kind: row.kind,
            id: row.ref,
            title: row.title,
            status: row.status,
            text: row.text,
            unavailableReason: row.unavailableReason,
        })),
        ...gathered.associationRetrieval
            .filter((row) => row.unavailableReason !== "restricted")
            .map((row) => ({
            citationId: associationCitationId(row.objectType, row.objectId),
            layer: "association",
            kind: row.objectType,
            id: row.objectId,
            title: row.title,
            status: row.status,
            text: row.text,
            unavailableReason: row.unavailableReason,
        })),
    ];
    const packed = applyRetrievalBudget(retrievalExcerpts, budget);
    const canonical = {
        identity: {
            citationId: subjectIdentityCitationId(subjectId),
            id: subjectId,
            label: gathered.node.label,
            kind: gathered.node.kind,
            aliases: gathered.node.aliases,
            status: gathered.node.status,
        },
        summary: {
            citationId: subjectSummaryCitationId(subjectId),
            text: gathered.node.context.summary,
        },
        body: {
            citationId: subjectBodyCitationId(subjectId),
            text: gathered.node.context.body,
        },
        superseded: gathered.node.context.superseded,
        facts: gathered.node.context.facts.map((fact) => ({
            ...fact,
            citationId: factCitationId(fact),
        })),
        decisions: gathered.node.context.decisions.map((decision, index) => ({
            ...decision,
            citationId: decisionCitationId(index, decision.at),
        })),
        openQuestions: related.filter((row) => row.kind === "open-question" && row.relation === "questions"),
        related,
        evidence,
        associations,
        maps: gathered.maps.map((map) => ({
            citationId: mapCitationId(map.mapId),
            mapId: map.mapId,
            title: map.title,
        })),
        clippings: gathered.node.context.clippings.map((clipping) => ({
            ...clipping,
            citationId: clippingCitationId(clipping.id),
        })),
        chatExcerpts: gathered.node.context.chatExcerpts.map((excerpt, index) => ({
            ...excerpt,
            citationId: chatExcerptCitationId(excerpt.sessionId, index),
        })),
    };
    const connected = gathered.connected ?? emptyConnectedKnowledge(subjectId);
    const intelligence = deriveSubjectIntelligence({
        subjectId,
        generatedAt: gathered.generatedAt,
        canonical,
        sources: gathered.intelligenceSources,
        connected,
    });
    return SubjectContextBundleSchema.parse({
        bundleVersion: SUBJECT_CONTEXT_BUNDLE_VERSION,
        subjectId,
        generatedAt: gathered.generatedAt,
        truncated: packed.truncated,
        depth: gathered.depth,
        retrievalCharBudget: budget,
        canonical,
        retrieval: { excerpts: packed.excerpts },
        intelligence,
        connected,
    });
}
export function neighbourExcerptFromContext(summary, body) {
    const source = summary.trim() || body.trim();
    return clipExcerpt(source, SUBJECT_CONTEXT_NEIGHBOUR_EXCERPT_CHARS);
}
//# sourceMappingURL=subject-context-bundle.js.map